/**
 * HIDDEN AETHER — brand mark and wordmark.
 *
 * The mark is a hand-tuned SVG signet: a thick crimson vault embossed
 * with a beveled gold "Æ" ligature (the ash, an old typographic device
 * still used in scientific Latin — perfect for "Aether"). The ligature
 * is set in Cinzel 900, painted with a four-stop metallic gradient and
 * underscored by an ember dot.
 *
 * The wordmark is set in Cinzel Decorative 900 with a metallic gold
 * gradient text-fill, flanked by diamond bullets — engraved bezel style.
 *
 * Usage:
 *   <BrandMark size={32} />                            // mark only
 *   <BrandWordmark size="lg" tagline="Mentalist" aura /> // mark + wordmark
 */
const ACCENT = "#d6c98c";  // champagne gold (inner detail)
const CREAM  = "#f0ead4";  // cream (wordmark fallback)
const EMBER  = "#c8202c";  // deep crimson — the aether spark
const RUBY   = "#8a1622";  // deeper red for the outer vault

// Mark is rendered on a 40-unit canvas; a stable id prefix keeps the
// gradient defs unique even if multiple marks live in the same DOM.
let MARK_ID = 0;

export function BrandMark({ size = 32, accent = ACCENT, className = "", breath = true }) {
  const s = size;
  // Unique ids per instance so several marks on one page don't collide.
  const uid = `m${++MARK_ID}`;
  const goldFill   = `url(#${uid}-gold)`;
  const goldStroke = `url(#${uid}-goldStroke)`;
  const crimsonFill = `url(#${uid}-crimson)`;

  const svg = (
    <svg
      width={s}
      height={s}
      viewBox="0 0 40 40"
      fill="none"
      role="img"
      aria-label="Hidden Aether"
      className={className}
      data-testid="brand-mark"
    >
      <defs>
        {/* Vertical metallic gold — top highlight, deep mid, bottom relight */}
        <linearGradient id={`${uid}-gold`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#fff6c8" />
          <stop offset="28%"  stopColor="#e9d99c" />
          <stop offset="55%"  stopColor="#8b6f2a" />
          <stop offset="78%"  stopColor="#c9b265" />
          <stop offset="100%" stopColor="#fff0b8" />
        </linearGradient>
        {/* Sharper gold stroke (less midtone shadow) for crisp edges */}
        <linearGradient id={`${uid}-goldStroke`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#fff6c8" />
          <stop offset="50%"  stopColor="#d6c98c" />
          <stop offset="100%" stopColor="#7a6326" />
        </linearGradient>
        {/* Crimson bevel — diagonal top-left light to bottom-right dark */}
        <linearGradient id={`${uid}-crimson`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"   stopColor="#d8323c" />
          <stop offset="55%"  stopColor="#a11421" />
          <stop offset="100%" stopColor="#4a060d" />
        </linearGradient>
      </defs>

      {/* Outer crimson vault — solid filled diamond with gold piping */}
      <path
        d="M20 1.8 L38.2 20 L20 38.2 L1.8 20 Z"
        fill={crimsonFill}
        stroke={goldStroke}
        strokeWidth="0.9"
        strokeLinejoin="miter"
      />
      {/* Outer ember rim — additional thin crimson highlight */}
      <path
        d="M20 1.8 L38.2 20 L20 38.2 L1.8 20 Z"
        fill="none"
        stroke={EMBER}
        strokeOpacity="0.55"
        strokeWidth="0.45"
        strokeLinejoin="miter"
      />
      {/* Inner inscribed diamond — gold filigree on dark backplate */}
      <path
        d="M20 7.4 L32.6 20 L20 32.6 L7.4 20 Z"
        fill="rgba(8,2,4,0.55)"
        stroke={goldStroke}
        strokeWidth="0.7"
        strokeLinejoin="miter"
      />
      {/* The Æ ligature — engraved gold signet at the heart.
          Cinzel 900, painted with the gold gradient. */}
      <text
        x="20"
        y="24.6"
        textAnchor="middle"
        fontFamily='"Cinzel", "Cinzel Decorative", "Cormorant Garamond", Georgia, serif'
        fontWeight="900"
        fontSize="15"
        fill={goldFill}
        stroke={EMBER}
        strokeWidth="0.18"
        strokeOpacity="0.7"
        style={{ letterSpacing: "-0.04em" }}
      >
        Æ
      </text>
      {/* Ember dot — the aether spark, sitting just below the ligature */}
      <circle
        cx="20"
        cy="29.6"
        r="1.4"
        fill={EMBER}
        className="mr-ember"
        style={{ transformOrigin: "20px 29.6px" }}
      />
      <circle
        cx="20"
        cy="29.6"
        r="2.4"
        fill={EMBER}
        fillOpacity="0.18"
        className="mr-ember-halo"
        style={{ transformOrigin: "20px 29.6px" }}
      />
      {/* Faint gold serif rule across the waist — adds bezel definition */}
      <line x1="9" y1="20" x2="14.5" y2="20" stroke={goldStroke} strokeOpacity="0.55" strokeWidth="0.35" strokeLinecap="round" />
      <line x1="25.5" y1="20" x2="31" y2="20" stroke={goldStroke} strokeOpacity="0.55" strokeWidth="0.35" strokeLinecap="round" />
    </svg>
  );

  if (!breath) return svg;
  return (
    <span
      className="mr-mark-breath inline-flex items-center justify-center"
      style={{ width: s, height: s, lineHeight: 0 }}
    >
      {svg}
    </span>
  );
}

/**
 * Full lockup — mark + wordmark, with optional micro tagline beneath.
 * size = "sm" | "md" | "lg" | "xl"
 * aura = render the slow rotating gold aura behind the mark.
 */
export function BrandWordmark({
  size = "md",
  tagline,
  className = "",
  align = "center",
  aura,
}) {
  const cfg = {
    sm: { mark: 32, word: "text-[14px]",  track: "tracking-[0.22em]", gap: "gap-2.5", tag: "text-[9px]",  bullet: 4 },
    md: { mark: 44, word: "text-[17px]",  track: "tracking-[0.26em]", gap: "gap-3",   tag: "text-[10px]", bullet: 5 },
    lg: { mark: 72, word: "text-[22px]",  track: "tracking-[0.28em]", gap: "gap-4",   tag: "text-[11px]", bullet: 6 },
    xl: { mark: 108, word: "text-[28px]", track: "tracking-[0.30em]", gap: "gap-5",   tag: "text-[12px]", bullet: 7 },
  }[size] || { mark: 44, word: "text-[17px]", track: "tracking-[0.26em]", gap: "gap-3", tag: "text-[10px]", bullet: 5 };

  const horizontal = size === "sm";
  const showAura = aura === undefined ? true : !!aura;

  return (
    <div
      className={`inline-flex ${horizontal ? "flex-row items-center" : "flex-col items-center"} ${cfg.gap} ${
        align === "left" ? "items-start" : ""
      } ${className}`}
      data-testid="brand-wordmark"
    >
      <div className={`relative flex items-center justify-center`} style={{ width: cfg.mark, height: cfg.mark }}>
        {showAura && <span className="mr-aura" aria-hidden="true" />}
        <span className="relative" style={{ zIndex: 1 }}>
          <BrandMark size={cfg.mark} />
        </span>
      </div>
        <div className={`flex items-center ${horizontal ? "" : "justify-center"} ${cfg.gap}`} style={{ width: "100%", justifyContent: "center" }}>
          {/* Left diamond bullet — engraved bezel ornament */}
          <Bullet size={cfg.bullet} />
          <span
            className={`${cfg.word} ${cfg.track} uppercase mr-sheen brand-wordmark-text`}
            style={{
              color: CREAM,
              fontFamily:
                '"Cinzel", "Cormorant Garamond", Georgia, "Times New Roman", serif',
              fontWeight: 500,
              textRendering: "optimizeLegibility",
              // Subtle inner shadow only — keeps the metal feel without the bulk.
              textShadow:
                "0 1px 0 rgba(0,0,0,0.5), 0 0 10px rgba(214,201,140,0.18)",
            }}
          >
            Hidden Aether
          </span>
          {/* Right diamond bullet */}
          <Bullet size={cfg.bullet} />
        </div>
      {tagline && (
        <span
          className={`${cfg.tag} uppercase tracking-[0.6em] font-semibold`}
          style={{
            color: EMBER,
            opacity: 0.9,
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontStyle: "italic",
            letterSpacing: "0.42em",
          }}
        >
          {tagline}
        </span>
      )}
    </div>
  );
}

/** Tiny diamond bullet that flanks the wordmark — a beveled gold gem. */
function Bullet({ size = 6 }) {
  const s = size;
  return (
    <svg width={s} height={s * 1.4} viewBox="0 0 10 14" aria-hidden="true" style={{ flexShrink: 0 }}>
      <defs>
        <linearGradient id={`bul-${s}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#fff6c8" />
          <stop offset="50%"  stopColor="#d6c98c" />
          <stop offset="100%" stopColor="#8b6f2a" />
        </linearGradient>
      </defs>
      <path d="M5 0.5 L9.5 7 L5 13.5 L0.5 7 Z" fill={`url(#bul-${s})`} stroke="#7a6326" strokeWidth="0.4" />
    </svg>
  );
}
